import { Text } from "@mantine/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { repositoryPos } from "../../../../_base/_const/_constVar";
import ImportExcelShell from "../../../../common/ImportExcel/ImportExcelShell";
import {
  ImportExcelColumn,
  ImportExcelRowErrors,
  ImportExcelSelectOption,
} from "../../../../common/ImportExcel/ImportExcelTypes";
import {
  parseExcelBoolean,
  toImportSubmitResult,
} from "../../../../common/ImportExcel/importExcelHelper";
import { MessageResponse } from "../../../../model/MessageResponse";

type BranchOption = ImportExcelSelectOption & {
  code: string;
  /** Mã chi nhánh trên ERP Asia, lấy từ danh mục để hiển thị kèm */
  asiaCode: string;
  /** CompanyCode của chi nhánh (attr6 của get-select-branch) */
  companyCode: string;
};

const normalizeCode = (value: any) => String(value ?? "").trim().toUpperCase();

const sampleRows = [
  {
    idBranch: "01",
    code: "K01_MAU",
    name: "KHO mẫu Hai Bà Trưng",
    attribute7: "K01MAU",
    addr: "Số 131 Lê Thanh Nghị",
    phone: "02471234567",
    note: "",
    active: "Đang sử dụng",
  },
];

const ImportExcelView = ({ onSuccess }: { onSuccess: () => void }) => {
  const [loadingReferenceData, setLoadingReferenceData] = useState(true);
  const branchesRef = useRef<BranchOption[]>([]);

  /**
   * Lấy toàn bộ danh mục chi nhánh (không truyền auth để không bị giới hạn
   * theo phân quyền chi nhánh của người đang đăng nhập).
   */
  const fetchBranches = async () => {
    try {
      const response = await repositoryPos.get<MessageResponse<any[]>>(
        `api/v1/TblDmInventory/get-select-branch`
      );
      if (response && response.success) {
        branchesRef.current = (response.data ?? [])
          .filter((item: any) => item.value && item.att2 && item.value !== "-1")
          .map((item: any) => ({
            value: String(item.value),
            code: String(item.att2),
            asiaCode: String(item.attr9 ?? ""),
            companyCode: String(item.attr6 ?? ""),
            label: `[${item.att2}] ${item.text ?? ""}`.trim(),
          }));
      }
    } finally {
      setLoadingReferenceData(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  /**
   * Cột "Mã chi nhánh" trên file excel nhập theo MÃ, nhưng trên bảng xem trước
   * là ô chọn (select) và giá trị lưu là ID chi nhánh - đúng thứ api cần.
   * Mã đọc từ excel chỉ dùng để dò ra id và để báo lỗi khi không khớp.
   */
  const columns = useMemo<ImportExcelColumn[]>(
    () => [
      {
        key: "idBranch",
        header: "Mã chi nhánh",
        required: true,
        type: "select",
        size: 250,
        placeholder: "Chọn chi nhánh",
        options: () => branchesRef.current,
        description:
          "Nhập mã chi nhánh (ví dụ 01), hệ thống dò sang danh mục và cho chọn lại nếu không khớp",
      },
      {
        key: "branchAsiaCode",
        header: "Mã chi nhánh Asia",
        size: 170,
        inTemplate: false,
        editable: false,
        description: "Lấy theo chi nhánh đã chọn, chỉ để đối chiếu",
      },
      {
        key: "code",
        header: "Mã kho",
        required: true,
        size: 120,
        description: "Tối đa 10 ký tự, không có khoảng trắng",
      },
      {
        key: "name",
        header: "Tên kho",
        required: true,
        size: 240,
        description: "Từ 5 đến 100 ký tự",
      },
      {
        key: "attribute7",
        header: "Mã kho Asia",
        size: 170,
        description: "Mã kho bên ERP Asia, không bắt buộc",
      },
      { key: "addr", header: "Địa chỉ", size: 220 },
      { key: "phone", header: "Điện thoại", size: 140 },
      { key: "note", header: "Ghi chú", size: 200 },
      {
        key: "active",
        header: "Sử dụng",
        type: "boolean",
        size: 150,
        description: "Đang sử dụng | Dừng sử dụng, để trống sẽ lấy Đang sử dụng",
      },
    ],
    []
  );

  const mapRow = useCallback((raw: Record<string, any>) => {
    // Giá trị đọc từ excel của cột chi nhánh là MÃ, giữ lại để dò và để báo lỗi
    const branchRawCode = String(raw.idBranch ?? "").trim();
    const branch = branchesRef.current.find(
      (b) => normalizeCode(b.code) === normalizeCode(branchRawCode)
    );

    return {
      branchRawCode,
      idBranch: branch?.value ?? null,
      branchAsiaCode: branch?.asiaCode ?? "",
      code: raw.code ?? "",
      attribute7: raw.attribute7 ?? "",
      name: raw.name ?? "",
      addr: raw.addr ?? "",
      phone: raw.phone ?? "",
      note: raw.note ?? "",
      active: parseExcelBoolean(raw.active, true),
    };
  }, []);

  const resolveRow = useCallback((row: Record<string, any>) => {
    const branch = branchesRef.current.find(
      (b) => String(b.value) === String(row.idBranch)
    );
    return { ...row, branchAsiaCode: branch?.asiaCode ?? "" };
  }, []);

  const validateRows = useCallback(
    (rows: Record<string, any>[]): ImportExcelRowErrors => {
      const errors: ImportExcelRowErrors = {};
      const codeSeen = new Map<string, number>();

      const addError = (index: number, key: string, message: string) => {
        if (!errors[index]) errors[index] = {};
        if (!errors[index][key]) errors[index][key] = message;
      };

      rows.forEach((row, index) => {
        const code = String(row.code ?? "").trim();
        const name = String(row.name ?? "").trim();

        if (!row.idBranch)
          addError(
            index,
            "idBranch",
            String(row.branchRawCode ?? "").length > 0
              ? `Không khớp mã chi nhánh "${row.branchRawCode}", vui lòng chọn lại`
              : "Chưa chọn chi nhánh"
          );

        if (code.length === 0) addError(index, "code", "Chưa nhập mã kho");
        else if (code.includes(" "))
          addError(index, "code", "Mã kho không được có khoảng trắng");
        else if (code.length > 10)
          addError(index, "code", "Mã kho tối đa 10 ký tự");
        else {
          const first = codeSeen.get(code.toLowerCase());
          if (first !== undefined)
            addError(index, "code", `Trùng mã kho với dòng ${first + 1}`);
          else codeSeen.set(code.toLowerCase(), index);
        }

        // Tên kho được phép trùng nhau, chỉ kiểm tra độ dài
        if (name.length === 0) addError(index, "name", "Chưa nhập tên kho");
        else if (name.length < 5 || name.length > 100)
          addError(index, "name", "Tên kho phải từ 5 đến 100 ký tự");

        if (String(row.attribute7 ?? "").trim().length > 200)
          addError(index, "attribute7", "Mã kho Asia tối đa 200 ký tự");
        // kho thuộc chi nhánh của công ty DVT bắt buộc phải có mã bên ERP Asia
        else if (
          branchesRef.current.find(
            (b) => String(b.value) === String(row.idBranch)
          )?.companyCode === "DVT" &&
          String(row.attribute7 ?? "").trim().length === 0
        )
          addError(
            index,
            "attribute7",
            "Kho thuộc chi nhánh công ty DVT phải nhập mã kho Asia"
          );

        if (String(row.addr ?? "").trim().length > 250)
          addError(index, "addr", "Địa chỉ tối đa 250 ký tự");

        if (String(row.note ?? "").trim().length > 250)
          addError(index, "note", "Ghi chú tối đa 250 ký tự");
      });

      return errors;
    },
    []
  );

  const onSubmit = useCallback(async (rows: Record<string, any>[]) => {
    const payload = rows.map((row) => ({
      id: 0,
      // api chỉ nhận id chi nhánh
      idBranch: Number(row.idBranch),
      code: String(row.code ?? "").trim(),
      name: String(row.name ?? "").trim(),
      // mã kho trên hệ thống ERP Asia
      attribute7: String(row.attribute7 ?? "").trim() || null,
      addr: String(row.addr ?? "").trim() || null,
      type: null,
      phone: String(row.phone ?? "").trim() || null,
      fax: null,
      note: String(row.note ?? "").trim() || null,
      active: Boolean(row.active),
      closeBookDate: null,
      isPrintInvoice: null,
      isPrintReceve: null,
      synDate: null,
      oldInventoryCode: null,
      codeoracle: null,
      positionId: null,
    }));

    const response = await repositoryPos.post(
      "/api/v1/TblDmInventory/create-list",
      payload
    );

    return toImportSubmitResult(response);
  }, []);

  return (
    <ImportExcelShell
      columns={columns}
      templateFileName="mau-import-danh-muc-kho.xlsx"
      sampleRows={sampleRows}
      mapRow={mapRow}
      resolveRow={resolveRow}
      validateRows={validateRows}
      onSubmit={onSubmit}
      onSuccess={onSuccess}
      loadingReferenceData={loadingReferenceData}
      hint={
        <Text size="xs">
          Cột <b>Mã chi nhánh</b> nhập theo mã trong danh mục chi nhánh, hệ thống
          sẽ dò sang danh mục để lấy id. Dòng nào không khớp sẽ báo đỏ và bạn chọn
          lại chi nhánh ngay trên bảng xem trước. Kho thuộc chi nhánh của công
          ty <b>DVT</b> bắt buộc phải có <b>Mã kho Asia</b>.
        </Text>
      }
    />
  );
};

export default ImportExcelView;
